// Types for Trips Module

export type AssignmentStatus = "pending" | "active" | "completed" | "cancelled";

// Timezone codes for assignments
//
// Las claves son identificadores IANA y viajan CRUDAS a Firestore:
// `truck_yards.timeZone`, `geofences.timezone` y `trip.assignments[].timezone`
// guardan literalmente lo que se elija acá, y nadie las valida al escribir. Una
// clave mal escrita no falla al guardar: falla después, en cada cálculo de día.
// Hay un test que las recorre todas contra `Intl` (timezoneOptions.test.ts):
// 'America/El_Paso' —que no existe— estuvo acá desde el 30/4/2026 (b8481468)
// hasta #517, cuatro meses, sin que nada lo detectara.
export const TIMEZONE_OPTIONS = {
  // Mexico
  'America/Mexico_City': 'MX - Ciudad de México',
  'America/Cancun': 'MX - Cancún (Quintana Roo)',
  'America/Tijuana': 'MX - Tijuana (Baja California)',
  'America/Hermosillo': 'MX - Hermosillo (Sonora)',
  'America/Monterrey': 'MX - Monterrey',
  'America/Merida': 'MX - Mérida',
  'America/Chihuahua': 'MX - Chihuahua',
  // Ciudad Juárez NO es la misma hora que el resto de Chihuahua. México quitó
  // el horario de verano en 2022 y los municipios fronterizos lo conservaron
  // para no desalinearse de El Paso, así que tzdata les dio zona propia desde
  // 2022g. La diferencia es de INVIERNO: medido igual bajo node 20.19.2
  // (ICU 76.1, tzdata 2025a — la familia de las Cloud Functions) y node 24.16.0
  // (ICU 78.3, tzdata 2026b),
  //   15/1/2026 12:00Z → Ciudad_Juarez 5:00 MST | Chihuahua 6:00 CST
  //    1/7/2026 12:00Z → Ciudad_Juarez 6:00 MDT | Chihuahua 6:00 CST
  // Elegir 'America/Chihuahua' para un patio de Ciudad Juárez corre el corte
  // del día 1 h de noviembre a marzo y clava exacto el resto del año, que es la
  // peor forma de estar mal: no se nota cuando se configura.
  'America/Ciudad_Juarez': 'MX - Ciudad Juárez (frontera)',
  // USA
  'America/New_York': 'US - Eastern Time',
  'America/Chicago': 'US - Central Time',
  // El Paso, Texas es huso de montaña, no central como el resto del estado, así
  // que su zona IANA ES ésta: 'America/Denver'. La entrada anterior
  // ('America/El_Paso', #517) no era una zona con otro nombre, no existía.
  // Va nombrada en la etiqueta para que quien buscaba "El Paso" en el <select>
  // siga encontrando algo, ahora válido; no se agrega una clave aparte porque
  // sería un duplicado exacto de ésta.
  'America/Denver': 'US - Mountain Time (Denver, El Paso)',
  'America/Los_Angeles': 'US - Pacific Time',
  'America/Phoenix': 'US - Arizona',
  'America/Anchorage': 'US - Alaska',
  'Pacific/Honolulu': 'US - Hawaii',
  'America/Detroit': 'US - Detroit',
  'America/Indiana/Indianapolis': 'US - Indiana',
  'America/Boise': 'US - Boise',
  // Latin America
  'America/Caracas': 'VE - Venezuela',
  'America/La_Paz': 'BO - Bolivia',
  'America/Bogota': 'CO - Colombia',
  'America/Lima': 'PE - Perú',
  'America/Santiago': 'CL - Chile',
  'America/Argentina/Buenos_Aires': 'AR - Argentina',
  'America/Sao_Paulo': 'BR - Brasil',
  'America/Guayaquil': 'EC - Ecuador',
  'America/Asuncion': 'PY - Paraguay',
  'America/Montevideo': 'UY - Uruguay',
  'America/Panama': 'PA - Panamá',
  'America/Guatemala': 'GT - Guatemala',
  'America/Managua': 'NI - Nicaragua',
  'America/Tegucigalpa': 'HN - Honduras',
  'America/El_Salvador': 'SV - El Salvador',
  'America/Costa_Rica': 'CR - Costa Rica',
  'America/Santo_Domingo': 'DO - República Dominicana',
  'America/Havana': 'CU - Cuba',
  // Canada
  'America/Toronto': 'CA - Toronto',
  'America/Vancouver': 'CA - Vancouver',
  'America/Edmonton': 'CA - Edmonton',
  'America/Winnipeg': 'CA - Winnipeg',
  // Europe
  'Europe/Madrid': 'ES - España',
  'Europe/London': 'UK - Reino Unido',
  'UTC': 'UTC',
} as const;

export type TimezoneCode = keyof typeof TIMEZONE_OPTIONS;

export interface TripAssignment {
  id?: string;
  device_id: string;
  date: string;           // "2026-04-20"
  start_time: string;     // "2026-04-20T08:00"
  end_time: string;       // "2026-04-20T10:00"
  status: AssignmentStatus;
  timezone?: TimezoneCode; // Timezone for this assignment
  device?: {
    id: string;
    name?: string;
    identifier?: string;
    vehicle_id?: string | null;
    remolque_id?: string | null;
    vehicle?: { id: string; name?: string } | null;
    remolque?: { id: string; name?: string } | null;
  } | null;
  /**
   * Conductor resuelto en backend (assignment.device → device.vehicle_id →
   * drivers → users). Lo usa el chat del viaje para preseleccionar al
   * driver como participante. Ver `tripsController._resolveDriversForVehicleIds`.
   */
  driver?: {
    id: string | null;
    user_id: string | null;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
}

export interface Trip {
  id: string;
  title?: string;
  description?: string;
  route_id?: string;
  status_id?: string;
  priority?: string;
  company_id?: string;
  comment?: string;
  assignments?: TripAssignment[];
  notifiedUsers?: string[];
  sendEmails?: boolean;
  createdAt?: { _seconds: number; _nanoseconds: number } | string;
  updatedAt?: { _seconds: number; _nanoseconds: number } | string;
  // Populated relations
  status?: {
    id: string;
    name?: string;
    color?: string;
  } | null;
  route?: {
    id: string;
    name?: string;
    description?: string;
  } | null;
  // Legacy fields (kept for backwards compatibility)
  origin?: string;
  destiny?: string;
  eta?: string;
  vehicle_id?: string;
  remolque_id?: string;
  geofence_ids?: string[];
  geofence_id?: string;
  charge_ids?: string[];
  user_id?: string;
  experience?: string;
  signature?: string;
  startedAt?: string;
  finalizedAt?: string;
  vehicle?: {
    id: string;
    name?: string;
    plate?: string;
    image?: string;
  } | null;
  remolque?: {
    id: string;
    name?: string;
    plate?: string;
  } | null;
  geofence?: {
    id: string;
    name?: string;
    shape?: string;
  } | null;
}

export interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

export interface TripAssignmentFormData {
  device_id: string;
  date: string;
  start_time: string;
  end_time: string;
  status?: AssignmentStatus;
  timezone?: TimezoneCode;
}

export interface TripFormData {
  title: string;
  description?: string;
  route_id: string;
  status_id: string;
  priority: string;
  assignments?: TripAssignmentFormData[];
  notifiedUsers?: string[];
  sendEmails?: boolean;
  comment?: string;
}

export interface FilterParams {
  page: number;
  limit: number;
  status_id?: string;
  search?: string;
  device_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface TripMeta {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  itemsPerPage: number;
}

export type ViewType = "grid" | "list";
export type TripPriority = "low" | "medium" | "high" | "urgent";
